import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { PageContainer, CommonCss, Card } from 'styles/common.styles';
import { SEARCH_LABEL_W } from 'styles/theme';
import { FormArea, CollapsibleSearchPanel, ActionButtonGroup, getCurrentFiscalYear } from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import {
  GridExportProvider,
  GridExportFocusScope,
  useRegisterGridExport,
  useExcelExportAction,
} from 'components/grid/excel';
import {
  ActiveGridProvider,
  ActiveGridScope,
  ActiveGridHeader,
  useActiveGridCrudActions,
} from 'contexts/ActiveGridContext';
import { showAlert } from 'utils/toastBridge';
import {
  YN_OPTIONS,
  REPORT_CLASS_OPTIONS,
  createFundUid,
  nextSortOrderBy10,
  loadFundGroups,
  fundGroupOptions,
  loadFunds,
  filterFunds,
  validateFunds,
  persistFundsWithHistory,
  loadFundHistory,
  enrichFundRows,
} from 'budget/budgetFundStore';

const FUND_GRID_ID = 'master-fund';
const HIST_GRID_ID = 'master-fund-history';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
`;

const Panel = styled(Card)`
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 0;
  overflow: hidden;
  flex: ${(p) => p.$flex || 1};
`;

const GridBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const Meta = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  margin-bottom: 4px;
  flex-shrink: 0;
`;

/**
 * 기준정보 — 재원등록
 * 편성·집행 금액 저장 단위(재원) + 이력
 */
const BudgetFundRegisterInner = () => {
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const { handleActiveAdd, handleActiveDelete, activeLabel, activeGridId } = useActiveGridCrudActions({
    guardAdd: (id) =>
      id === HIST_GRID_ID ? '이력은 조회 전용입니다. 재원 그리드에서 추가하세요.' : null,
    guardDelete: (id) => (id === HIST_GRID_ID ? '이력은 삭제할 수 없습니다.' : null),
  });
  const fundGridRef = useRef(null);
  const histGridRef = useRef(null);
  const [selectedFundUid, setSelectedFundUid] = useState('');
  const [selectedHint, setSelectedHint] = useState('재원을 선택하면 이력이 표시됩니다.');
  const [groupOpts, setGroupOpts] = useState([{ value: '', label: '선택' }]);
  const year = getCurrentFiscalYear();

  const searchDefaults = useMemo(
    () => ({
      tenantCode,
      fiscalYear: year,
      fundCode: '',
      fundName: '',
      fundGroupUid: '',
      reportClass: '',
      useYn: 'Y',
    }),
    [tenantCode, year]
  );

  const methods = useForm({ mode: 'onSubmit', defaultValues: searchDefaults });
  const { getValues, setValue } = methods;

  useEffect(() => {
    setValue('tenantCode', tenantCode);
  }, [tenantCode, setValue]);

  const getDims = useCallback(() => {
    const v = getValues();
    return {
      tenantCode: v.tenantCode || tenantCode,
      fiscalYear: v.fiscalYear || year,
    };
  }, [getValues, tenantCode, year]);

  const refreshGroupOpts = useCallback(() => {
    setGroupOpts(fundGroupOptions(getDims(), { includeEmpty: true }));
  }, [getDims]);

  const fundColumns = useMemo(
    () => [
      hiCol.edTextColumn({ header: '재원코드', field: 'fundCode', size: 100, required: true }),
      hiCol.edTextColumn({ header: '재원명', field: 'fundName', size: 120, required: true }),
      hiCol.edComboColumn({
        header: '재원그룹',
        field: 'fundGroupUid',
        size: 140,
        options: groupOpts,
        required: true,
      }),
      hiCol.edComboColumn({
        header: '대분류',
        field: 'reportClass',
        size: 100,
        options: [{ value: '', label: '-' }, ...REPORT_CLASS_OPTIONS],
      }),
      hiCol.edTextColumn({
        header: '외부코드',
        field: 'extCode',
        size: 100,
        title: 'e나라도움 등 외부 매핑 코드',
      }),
      hiCol.edComboColumn({ header: '사용', field: 'useYn', size: 80, options: YN_OPTIONS }),
      hiCol.edNumberColumn({ header: '정렬', field: 'sortOrder', size: 80, align: 'center' }),
      hiCol.edTextColumn({ header: '비고', field: 'remark', size: 160 }),
    ],
    [groupOpts]
  );

  const histColumns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '변경일시', field: 'changeAtDisp', size: 150 }),
      hiCol.roTextColumn({ header: '유형', field: 'changeTypeNm', size: 90, align: 'center' }),
      hiCol.roTextColumn({ header: '재원코드', field: 'fundCode', size: 90 }),
      hiCol.roTextColumn({ header: '재원명', field: 'fundName', size: 120 }),
      hiCol.roTextColumn({ header: '대분류', field: 'reportClassNm', size: 80, align: 'center' }),
      hiCol.roTextColumn({ header: '사용', field: 'useYn', size: 70, align: 'center' }),
      hiCol.roTextColumn({ header: '처리자', field: 'changeBy', size: 90 }),
      hiCol.roTextColumn({ header: '메모', field: 'memo', size: 200 }),
    ],
    []
  );

  const fundGrid = useHiGridHook({
    gridRef: fundGridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: true,
    defaultRow: {
      fundUid: '',
      fundCode: '',
      fundName: '',
      fundGroupUid: '',
      reportClass: '',
      useYn: 'Y',
      sortOrder: 10,
      extCode: '',
      remark: '',
    },
  });

  const histGrid = useHiGridHook({
    gridRef: histGridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: false,
    confirmCheck: false,
    defaultRow: {},
  });

  const pushHistory = useCallback(
    (fundUid, rowHint) => {
      if (!fundUid) {
        histGridRef.current?.setData?.([]);
        setSelectedHint('재원을 선택하면 이력이 표시됩니다.');
        return;
      }
      const tenant = getValues('tenantCode') || tenantCode;
      const list = loadFundHistory(tenant, fundUid).map((h) => ({
        ...h,
        fundCode: h.snapshot?.fundCode || '',
        fundName: h.snapshot?.fundName || '',
        useYn: h.snapshot?.useYn || '',
      }));
      histGridRef.current?.setData?.(list);
      if (rowHint) {
        setSelectedHint(
          `${rowHint.fundCode || ''} ${rowHint.fundName || ''} · ${rowHint.fundGroupNm || '—'}`
        );
      }
    },
    [getValues, tenantCode]
  );

  const selectFundRow = useCallback(
    (row) => {
      if (!row?.fundUid) return;
      setSelectedFundUid(row.fundUid);
      pushHistory(row.fundUid, row);
    },
    [pushHistory]
  );

  const handleSearch = useCallback(async () => {
    try {
      const dims = getDims();
      // loadFunds 내부에서 재원그룹까지 적재되므로 그룹 옵션은 조회 후 갱신한다.
      const all = await loadFunds(dims);
      refreshGroupOpts();
      const filtered = filterFunds(all, getValues());
      fundGridRef.current?.setData?.(filtered);
      const still = filtered.find((r) => r.fundUid === selectedFundUid);
      if (still) {
        pushHistory(still.fundUid, still);
      } else {
        setSelectedFundUid('');
        pushHistory('');
      }
    } catch (e) {
      showAlert(e?.message || '조회에 실패했습니다.', 'error');
    }
  }, [getDims, getValues, selectedFundUid, pushHistory, refreshGroupOpts]);

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 최초 조회
  }, []);

  useEffect(() => {
    fundGrid.onSelectRow = (row) => selectFundRow(row);
    fundGrid.onRowClicked = ({ data }) => selectFundRow(data);
  }, [fundGrid, selectFundRow]);

  const handleAdd = useCallback(async () => {
    const dims = getDims();
    const data = fundGridRef.current?.getData?.() || [];
    const groups = (await loadFundGroups(dims)).filter((g) => g.useYn !== 'N');
    const fundUid = createFundUid('FU');
    await fundGrid.handleAdd({
      fundUid,
      id: fundUid,
      fundCode: '',
      fundName: '',
      fundGroupUid: groups[0]?.fundGroupUid || '',
      reportClass: '',
      useYn: 'Y',
      sortOrder: nextSortOrderBy10(data),
      extCode: '',
      remark: '',
    });
  }, [fundGrid, getDims]);

  const handleDelete = useCallback(async () => {
    await fundGrid.handleDelete();
  }, [fundGrid]);

  const handleSave = useCallback(async () => {
    try {
      const dims = getDims();
      const groups = await loadFundGroups(dims);
      const gridData = fundGridRef.current?.getData?.() || [];
      const active = gridData.filter((r) => r.mod !== 'D');
      const v = validateFunds(active, groups);
      if (!v.ok) {
        showAlert(v.message, 'warning');
        return;
      }

      const all = await loadFunds(dims);
      const filterVals = getValues();
      const inFilterUids = new Set(filterFunds(all, filterVals).map((r) => r.fundUid));
      const outside = all.filter((r) => !inFilterUids.has(r.fundUid));
      const after = enrichFundRows([...outside, ...active], groups);

      const { saved, historyTouched } = await persistFundsWithHistory(dims, after, {
        userId: user?.id || user?.username || '',
      });

      fundGridRef.current?.setData?.(filterFunds(saved, getValues()));
      if (selectedFundUid) {
        const row = saved.find((r) => r.fundUid === selectedFundUid);
        pushHistory(selectedFundUid, row);
      }
      showAlert(
        historyTouched > 0
          ? `저장했습니다. 이력 ${historyTouched}건이 기록되었습니다.`
          : '저장했습니다.',
        'success'
      );
    } catch (e) {
      showAlert(e?.message || '저장에 실패했습니다.', 'error');
    }
  }, [getDims, getValues, user, selectedFundUid, pushHistory]);

  const searchFields = useMemo(
    () => [
      [
        {
          name: 'fiscalYear',
          label: '회계연도',
          type: 'fiscalYear',
          width: CommonCss.w80,
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'fundCode',
          label: '재원코드',
          type: 'text',
          width: CommonCss.w90,
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'fundName',
          label: '재원명',
          type: 'text',
          width: CommonCss.w100,
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'fundGroupUid',
          label: '그룹',
          type: 'select',
          width: CommonCss.w120,
          labelWidth: SEARCH_LABEL_W,
          options: [{ value: '', label: '전체' }, ...groupOpts.filter((o) => o.value)],
        },
        {
          name: 'reportClass',
          label: '대분류',
          type: 'select',
          width: CommonCss.w90,
          labelWidth: SEARCH_LABEL_W,
          options: [{ value: '', label: '전체' }, ...REPORT_CLASS_OPTIONS],
        },
        {
          name: 'useYn',
          label: '사용',
          type: 'select',
          width: CommonCss.w70,
          labelWidth: SEARCH_LABEL_W,
          options: [{ value: '', label: '전체' }, ...YN_OPTIONS],
        },
      ],
    ],
    [groupOpts]
  );

  const getFundExportData = useCallback(
    () => fundGridRef.current?.getFilteredData?.() ?? fundGridRef.current?.getData?.() ?? [],
    []
  );
  const getFundExportCols = useCallback(() => fundGridRef.current?.getDataColumns?.() ?? [], []);
  const getHistExportData = useCallback(
    () => histGridRef.current?.getFilteredData?.() ?? histGridRef.current?.getData?.() ?? [],
    []
  );
  const getHistExportCols = useCallback(() => histGridRef.current?.getDataColumns?.() ?? [], []);

  useRegisterGridExport({
    id: FUND_GRID_ID,
    label: '재원',
    getData: getFundExportData,
    getDataColumns: getFundExportCols,
    joinKeys: ['fundUid'],
    order: 0,
  });
  useRegisterGridExport({
    id: HIST_GRID_ID,
    label: '재원이력',
    getData: getHistExportData,
    getDataColumns: getHistExportCols,
    joinKeys: ['histId'],
    order: 1,
  });

  const { openDialog: openExcelDialog, dialog: excelDialog } = useExcelExportAction({
    fileName: '재원등록',
  });

  const actionButtons = useMemo(
    () => [
      { id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7' },
      {
        id: 'add',
        label: '추가',
        icon: '➕',
        onClick: handleActiveAdd,
        hotkey: 'F8',
        title: activeLabel ? `추가 → ${activeLabel} (F8)` : '추가 (F8) · 그리드 선택 Alt+F2',
      },
      {
        id: 'delete',
        label: '삭제',
        icon: '❌',
        onClick: handleActiveDelete,
        hotkey: 'F9',
        variant: 'danger',
        title: activeLabel ? `삭제 → ${activeLabel} (F9)` : '삭제 (F9) · 그리드 선택 Alt+F2',
      },
      { id: 'save', label: '저장', icon: '✅', onClick: handleSave, hotkey: 'F10', variant: 'success' },
      { id: 'excel', label: '엑셀', icon: '📥', onClick: openExcelDialog, hotkey: 'F11' },
    ],
    [handleSearch, handleActiveAdd, handleActiveDelete, handleSave, openExcelDialog, activeLabel]
  );

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} />
        <CollapsibleSearchPanel
          storageKey="master-fund-register"
          prefsUserId={prefsUserId}
          summary="재원 조회"
        >
          <FormArea
            fields={searchFields}
            compact
            onFieldEnter={handleSearch}
            defaultValues={searchDefaults}
          />
        </CollapsibleSearchPanel>
      </FormProvider>

      <Meta>
        활성 그리드: <strong style={{ color: 'var(--erp-color-primary)' }}>{activeLabel || '—'}</strong>
        {' · '}
        Alt+F2 순환
        {activeGridId ? ` · F8/F9 → ${activeLabel}` : ' · Alt+F2로 그리드 선택 후 F8/F9'}
        {' · '}
        기관 {getValues('tenantCode') || tenantCode}
      </Meta>

      <Stack>
        <Panel $flex={1.4}>
          <ActiveGridHeader gridId={FUND_GRID_ID} extra="편성·집행 저장 단위 · 재원그룹 소속">
            재원
          </ActiveGridHeader>
          <GridBody>
            <ActiveGridScope
              gridId={FUND_GRID_ID}
              label="재원"
              order={0}
              onAdd={handleAdd}
              onDelete={handleDelete}
              style={{ flex: 1, minHeight: 0, height: '100%' }}
            >
              <GridExportFocusScope gridId={FUND_GRID_ID} style={{ flex: 1, minHeight: 0, height: '100%' }}>
                <HiGridComponent
                  ref={fundGridRef}
                  columns={fundColumns}
                  height="100%"
                  showRowNumber
                  rowNumberSize={56}
                  showModColumn
                  headerActive={activeGridId === FUND_GRID_ID}
                  {...fundGrid.contextMenuProps}
                  enableHeaderMenu
                  enableColumnFilters
                  enableFreeze
                  enableQuickExport
                  exportFileName="재원"
                  gridId={FUND_GRID_ID}
                  prefsUserId={prefsUserId}
                  colOptions={fundGrid.colOptions}
                />
              </GridExportFocusScope>
            </ActiveGridScope>
          </GridBody>
        </Panel>

        <Panel $flex={0.9}>
          <ActiveGridHeader gridId={HIST_GRID_ID} extra="신규·수정·그룹변경·폐지">
            재원 이력
          </ActiveGridHeader>
          <Meta style={{ padding: '4px 12px' }}>{selectedHint}</Meta>
          <GridBody>
            <ActiveGridScope
              gridId={HIST_GRID_ID}
              label="재원이력"
              order={1}
              canAdd={false}
              canDelete={false}
              style={{ flex: 1, minHeight: 0, height: '100%' }}
            >
              <GridExportFocusScope gridId={HIST_GRID_ID} style={{ flex: 1, minHeight: 0, height: '100%' }}>
                <HiGridComponent
                  ref={histGridRef}
                  columns={histColumns}
                  height="100%"
                  showRowNumber
                  rowNumberSize={48}
                  headerActive={activeGridId === HIST_GRID_ID}
                  {...histGrid.contextMenuProps}
                  enableHeaderMenu
                  enableColumnFilters
                  enableFreeze
                  enableQuickExport
                  exportFileName="재원이력"
                  gridId={HIST_GRID_ID}
                  prefsUserId={prefsUserId}
                />
              </GridExportFocusScope>
            </ActiveGridScope>
          </GridBody>
        </Panel>
      </Stack>

      {excelDialog}
    </PageContainer>
  );
};

const BudgetFundRegister = () => (
  <GridExportProvider>
    <ActiveGridProvider>
      <BudgetFundRegisterInner />
    </ActiveGridProvider>
  </GridExportProvider>
);

export default BudgetFundRegister;
